import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { setIsLoading } from "../../app/slices/GlobalSlice";
import {
  LanguageData,
  addAutoTranslatedLanguage,
  selectTrainingLocalizationState,
} from "../../app/slices/TrainingLocalizationSlice";
import {
  selectReferenceLanguage,
} from "../../app/slices/TrainingSlice";
import { TranslationService } from "../../services/TranslationService";
import { v4 as uuidv4 } from "uuid";
import {
  beginHistoryTransaction,
  cancelHistoryTransaction,
  commitHistoryTransaction,
} from "../../app/slices/HistorySlice";
import {
  TRANSLATION_FAILED_KEY,
  TRANSLATION_FAILURE_TOAST_MS,
} from "../../helpers/translationFeedback";

export const useAddLanguage = () => {
  const locState = useSelector(selectTrainingLocalizationState);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const token = useSelector(selectToken);
  const referenceLanguage = useSelector(selectReferenceLanguage);

  async function addNewLanguage(lang: LanguageData) {
    var from = referenceLanguage;
    var to = lang.shortcode;
    var texts: string[] = [];

    for (var key in locState.localizedEntries) {
      texts.push(locState.localizedEntries[key][from].value);
    }

    var translatedTexts = await TranslationService.TranslateTexts(
      token,
      texts,
      from,
      to
    );

    dispatch(
      addAutoTranslatedLanguage({
        lang: lang,
        translatedValues: translatedTexts,
      })
    );
  }

  async function addMultipleLanguages(langs: LanguageData[]) {
    const transaction = { id: `add-languages-${uuidv4()}` };
    dispatch(setIsLoading(true));
    dispatch(beginHistoryTransaction(transaction));

    try {
      for (var lang of langs) {
        await addNewLanguage(lang);
      }
      dispatch(commitHistoryTransaction(transaction));
    } catch (error) {
      // The transaction is cancelled, so the half-added languages are rolled
      // back. Reported rather than rethrown: the dialog fires this without
      // awaiting, so a rethrow would be an unhandled rejection and the editor
      // would be left looking at a language that never arrived.
      dispatch(cancelHistoryTransaction(transaction));
      toast.error(t(TRANSLATION_FAILED_KEY) as string, {
        position: "top-right",
        autoClose: TRANSLATION_FAILURE_TOAST_MS,
      });
    } finally {
      dispatch(setIsLoading(false));
    }
  }
  return { addNewLanguage, addMultipleLanguages } as const;
};
